/**
 * @property {string|null} id
 * @property {string|null} name
 * @property {Thematic[]} children
 * @property {Thematic|null} parent
 */
export default class Thematic {
    constructor() {
        this.id = null;
        this.name = null;
        this.children = [];
        this.parent = null;
    }

    /**
     * Returns a new thematics tree that only contains this thematic children with ID included in given
     * `filtered_thematic_ids`.
     *
     * Returned `Thematic` instances are **clones**.
     * @param {string[]} filtered_thematic_ids
     * @return {Readonly<Thematic[]>}
     */
    filterChildren(filtered_thematic_ids) {
        const out = [];

        this.children.forEach((child) => {
            const filteredChildren = child.filterChildren(filtered_thematic_ids);

            if (filtered_thematic_ids.includes(child.id) || filteredChildren.length > 0) {
                const clone = Object.assign(new Thematic(), child);
                clone.children = filteredChildren.map((child) => {
                    child.parent = clone;
                    return Object.freeze(child);
                });

                out.push(clone);
            }
        });

        return Object.freeze(out);
    }

    /**
     * Returns any child (at any level) corresponding to given `child_id`.
     * @param {string} child_id
     * @return {Thematic|null}
     */
    findChild(child_id) {
        if (typeof child_id !== "string") {
            throw new TypeError(`Thematic.findChild() method expects parameter 1 to be a string, ${typeof child_id} given`);
        }

        let out = null;

        for (let i = 0; i < this.children.length; i++) {
            if (this.children[i].id === child_id) {
                out = this.children[i];
                break;
            } else {
                out = this.children[i].findChild(child_id);

                if (out instanceof Thematic) {
                    break;
                }
            }
        }

        return out;
    }

    /**
     * Returns a new `Thematic` instance hydrated with data from given `api_resource`, and with given `parent`.
     * @param {object} api_resource
     * @param {Thematic} parent
     * @return {Thematic}
     */
    static fromAPI(api_resource, parent) {
        if (typeof api_resource !== "object") {
            throw new TypeError(`Thematic.fromAPI() method expects parameter 1 to be an object, ${typeof api_resource} given`);
        }

        const out = new Thematic();
        return Object.assign(out, {
            id: api_resource.id,
            name: api_resource.name,
            children: Object.freeze(api_resource.children.map((child) => {
                return Object.freeze(Thematic.fromAPI(child, out));
            })),
            parent: (parent instanceof Thematic ? parent : null)
        });
    }

    /**
     * Returns this thematic path
     * @return {Thematic[]}
     */
    get path() {
        const out = [];
        let cursor = this;

        while (cursor instanceof Thematic) {
            out.push(cursor);
            cursor = cursor.parent;
        }

        return out.reverse();
    }
}
